import {OtsBadge} from "../../components/OtsBadge";
import {OtsDateTime} from "../../components/OtsDateTime";
import {opsStatusTone} from "./map-panel";

/** A status as the panel shows it: the code picks the color, the label is already translated. */
export type OpsStatusLabel = {status: string; label: string};

export type OpsModeView = OpsStatusLabel & {mode: string; modeLabel: string; asOf?: number};
export type OpsFacetView = OpsStatusLabel & {facet: string; facetLabel: string; modes: OpsModeView[]};
export type OpsServiceTypeView = OpsStatusLabel & {id: string; name: string; facets: OpsFacetView[]};

/** A site's operational status (HubGo's ops mode), ready to draw. */
export type OpsStatusView = OpsStatusLabel & {
  /** The latest any mode reported. */
  asOf?: number;
  serviceTypes: OpsServiceTypeView[];
};

export const OpsStatusBadge = ({status, label}: OpsStatusLabel) => (
  <OtsBadge size="compact" tone={opsStatusTone(status)} dot>{label}</OtsBadge>
);

/** The latest report time across every mode, for "updated" lines. */
export const latestOpsAsOf = (serviceTypes: OpsServiceTypeView[]) => {
  let latest: number | undefined;
  for (const serviceType of serviceTypes)
    for (const facet of serviceType.facets)
      for (const mode of facet.modes)
        if (mode.asOf != null && (latest == null || mode.asOf > latest)) latest = mode.asOf;
  return latest;
};

/** Each service type with its facets (clearance, treatment...) and the modes reported for each. */
export const OpsServiceStatusList = ({serviceTypes}: {serviceTypes: OpsServiceTypeView[]}) => (
  <div className="flex flex-col gap-4">
    {serviceTypes.filter(serviceType => serviceType.facets.length > 0).map(serviceType => (
      <div key={serviceType.id} className="flex flex-col gap-2" data-service-type={serviceType.id}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[var(--ots-text)]">{serviceType.name}</span>
          <OpsStatusBadge status={serviceType.status} label={serviceType.label}/>
        </div>
        <ul className="flex flex-col gap-2 border-l-2 border-[var(--ots-outline-variant)] pl-3">
          {serviceType.facets.map(facet => (
            <li key={facet.facet} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-[var(--ots-secondary)]">{facet.facetLabel}</span>
                <OpsStatusBadge status={facet.status} label={facet.label}/>
              </div>
              {facet.modes.length > 0 && (
                <table className="w-full text-[11px]">
                  <thead className="sr-only">
                    <tr><th>Mode</th><th>Status</th><th>As of</th></tr>
                  </thead>
                  <tbody>
                    {facet.modes.map((mode, index) => (
                      <tr key={`${mode.mode}-${index}`} className="align-middle">
                        <td className="py-0.5 pr-2 text-[var(--ots-text)]">{mode.modeLabel}</td>
                        <td className="py-0.5 pr-2"><OpsStatusBadge status={mode.status} label={mode.label}/></td>
                        <td className="py-0.5 text-right text-[var(--ots-text-muted)]"><OtsDateTime value={mode.asOf}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);
